import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Link } from "react-router-dom";
import TableContainer from "Common/TableContainer";
import Select from "react-select";
import { Loader } from "../../Common/Loader";
// Icons
import { Calendar, Search, Users, BookOpen, Clock } from "lucide-react";
// API Helpers
import { getProgramList, getClassList, getMonthlyAttendance } from "helpers/backend_helper";
import { useProgramStore } from "store/useProgramStore";
import { useClassStore } from "store/useClassStore";

// Default options for dropdowns
const defaultProgramOption = { value: "", label: "All Programs" };
const defaultClassOption = { value: "", label: "All Classes" };

// Attendance type mapping
const getAttendanceDisplay = (attendanceType: number | null) => {
  switch (attendanceType) {
    case 1: return { icon: '✓', className: 'text-green-500' }; // Present
    case 2: return { icon: '✗', className: 'text-red-500' };   // Absent
    case 3: return { icon: 'H', className: 'text-blue-500' };  // Holiday
    case 4: return { icon: 'L', className: 'text-yellow-500' }; // Late
    default: return { icon: '-', className: 'text-gray-400' }; // No data
  }
};

function AttendanceList() {

    // Zustand stores
    const { 
      programs,
      selectedProgram, 
      setSelectedProgram,
      fetchPrograms
    } = useProgramStore();
    
    const { 
      classes,
      selectedClass, 
      setSelectedClass,
    } = useClassStore();
    
  const [attendanceData, setAttendanceData] = useState<any[]>([]);
  const [filteredAttendance, setFilteredAttendance] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');
  // const [selectedProgram, setSelectedProgram] = useState<any>(defaultProgramOption);
  // const [selectedClass, setSelectedClass] = useState<any>(defaultClassOption);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
  });

  // API Data States
  const [monthlyAttendanceData, setMonthlyAttendanceData] = useState<any[]>([]);
  const [isLoadingAttendance, setIsLoadingAttendance] = useState<boolean>(false);

  // Use Zustand store data directly - convert to dropdown format
  const programOptions = useMemo(() => {
    const options = programs.map((program: any) => ({
      value: program.programId,
      label: program.programName
    }));
    return [defaultProgramOption, ...options];
  }, [programs]);

  const classOptions = useMemo(() => {
    const options = classes.map((classItem: any) => ({
      value: classItem.classId,
      label: classItem.className
    }));
    return [defaultClassOption, ...options];
  }, [classes]);

  // Convert selected values from Zustand store to dropdown format
  const selectedProgramOption = useMemo(() => {
    if (!selectedProgram) return defaultProgramOption;
    return {
      value: selectedProgram.programId,
      label: selectedProgram.programName
    };
  }, [selectedProgram]);

  const selectedClassOption = useMemo(() => {
    if (!selectedClass) return defaultClassOption;
    return {
      value: selectedClass.classId,
      label: selectedClass.className
    };
  }, [selectedClass]);

  // Stats calculations
  const totalStudents = filteredAttendance.length;
  const currentDate = new Date();
  const today = currentDate.getDate().toString().padStart(2, '0');
  
  // Calculate today's attendance from API data
  const absentToday = filteredAttendance.filter(student => {
    const todayRecord = student.dailyRecords?.find((record: any) => {
      const recordDate = new Date(record.date);
      return recordDate.getDate().toString().padStart(2, '0') === today;
    });
    return todayRecord?.attendanceType === 2; // Absent
  }).length;
  
  const presentToday = filteredAttendance.filter(student => {
    const todayRecord = student.dailyRecords?.find((record: any) => {
      const recordDate = new Date(record.date);
      return recordDate.getDate().toString().padStart(2, '0') === today;
    });
    return todayRecord?.attendanceType === 1; // Present
  }).length;
  
  const workingDaysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();

  const handleProgramChange = (option: any) => {
    // Option is already in the format {value: programId, label: programName}
    if (option && option.value && option.value !== "") {
      // Find the program from Zustand store and update it
      const program = programs.find(p => p.programId === option.value);
      if (program) {
        setSelectedProgram(program);
        console.log("Program selected:", program);
      }
    } else {
      setSelectedProgram(null);
      console.log("Program cleared");
    }
  };

  const handleClassChange = (option: any) => {
    // Option is already in the format {value: classId, label: className}
    if (option && option.value && option.value !== "") {
      // Find the class from Zustand store and update it
      const classItem = classes.find(c => c.classId === option.value);
      if (classItem) {
        setSelectedClass(classItem);
        console.log("Class selected:", classItem);
      }
    } else {
      setSelectedClass(null);
      console.log("Class cleared");
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };


  // Load Monthly Attendance from API
  const loadMonthlyAttendance = async () => {
    if (!selectedProgram?.programId || !selectedClass?.classId || !selectedDate) {
      setMonthlyAttendanceData([]);
      setFilteredAttendance([]);
      return;
    }

    setIsLoadingAttendance(true);
    try {
      const [year, month] = selectedDate.split('-');
      const response = await getMonthlyAttendance({
        programId: selectedProgram.programId,
        classId: selectedClass.classId,
        month: parseInt(month),
        year: parseInt(year)
      });
      
      console.log("Monthly attendance response:", response);
      
      if (response.data && Array.isArray(response.data)) {
        setMonthlyAttendanceData(response.data);
        
        // Transform data for the table
        const transformedData = response.data.map((student: any) => {
          const studentData: any = {
            studentId: student.studentId,
            studentName: student.studentName,
            dailyRecords: student.dailyRecords
          };
          
          // Add daily attendance columns
          student.dailyRecords?.forEach((record: any) => {
            const date = new Date(record.date);
            const dayStr = date.getDate().toString().padStart(2, '0');
            const attendanceDisplay = getAttendanceDisplay(record.attendanceType);
            studentData[dayStr] = attendanceDisplay.icon;
            studentData[`${dayStr}_type`] = record.attendanceType;
            studentData[`${dayStr}_class`] = attendanceDisplay.className;
          });
          
          return studentData;
        });
        
        setAttendanceData(transformedData);
        setFilteredAttendance(transformedData);
      } else {
        setMonthlyAttendanceData([]);
        setAttendanceData([]);
        setFilteredAttendance([]);
      }
    } catch (error) {
      console.error("Error loading monthly attendance:", error);
      setMonthlyAttendanceData([]);
      setAttendanceData([]);
      setFilteredAttendance([]);
    } finally {
      setIsLoadingAttendance(false);
    }
  };

  useEffect(() => {
    // Load data from Zustand stores if not already loaded
    const loadData = async () => {
      console.log("Starting to load dropdown data...");
      

      
      // Ensure default selections are set if not already selected
      if (programs.length > 0 && !selectedProgram) {
        setSelectedProgram(programs[0]);
        console.log("Set default program:", programs[0]);
      }
      
      if (classes.length > 0 && !selectedClass) {
        setSelectedClass(classes[0]);
        console.log("Set default class:", classes[0]);
      }
      
      setIsLoading(false);
      console.log("Finished loading dropdown data");
    };

    loadData();
  }, [programs.length, classes.length, selectedProgram, selectedClass,  setSelectedProgram, setSelectedClass]);

  // Load attendance data when filters change
  useEffect(() => {
    if (selectedProgram?.programId && selectedClass?.classId && selectedDate) {
      loadMonthlyAttendance();
    } else {
      setAttendanceData([]);
      setFilteredAttendance([]);
    }
  }, [selectedProgram, selectedClass, selectedDate]);

  useEffect(() => {
    // Filter attendance data based on search text
    const filtered = attendanceData.filter((student: any) => {
      const searchMatch = searchText === "" || 
        student.studentName.toLowerCase().includes(searchText.toLowerCase());
      return searchMatch;
    });
    setFilteredAttendance(filtered);
  }, [attendanceData, searchText]);

  // Debug effect to log Zustand store changes
  useEffect(() => {
    console.log("Program store updated:", { programs, selectedProgram });
  }, [programs, selectedProgram]);

  useEffect(() => {
    console.log("Class store updated:", { classes, selectedClass });
  }, [classes, selectedClass]);

  // Ensure default selections when stores are loaded
  useEffect(() => {
    if (programs.length > 0 && !selectedProgram) {
      setSelectedProgram(programs[0]);
      console.log("Auto-selected first program:", programs[0]);
    }
  }, [programs, selectedProgram, setSelectedProgram]);

  useEffect(() => {
    if (classes.length > 0 && !selectedClass) {
      setSelectedClass(classes[0]);
      console.log("Auto-selected first class:", classes[0]);
    }
  }, [classes, selectedClass, setSelectedClass]);

  // Generate columns for the table
  const columns = useMemo(() => {
    if (!selectedDate) return [];
    
    const [year, month] = selectedDate.split('-');
    const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate();
    
    const cols = [
      {
        header: "Student Name",
        accessorKey: "studentName",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <div className="font-medium text-slate-900 dark:text-zink-50">
            {cell.getValue()}
          </div>
        ),
      }
    ];

    // Add columns for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayStr = day.toString().padStart(2, '0');
      cols.push({
        header: dayStr,
        accessorKey: dayStr,
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          const row = cell.row.original;
          const className = row[`${dayStr}_class`] || "text-gray-400";
          
          return (
            <div className={`text-center font-medium ${className}`}>
              {value || '-'}
            </div>
          );
        },
      });
    }

    return cols;
  }, [selectedDate]);

  return (
    <React.Fragment>
     
      <div className="container-fluid group-data-[content=boxed]:max-w-boxed mx-auto">
        <div className="flex flex-col gap-2 py-4 md:py-6">
          
          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2 xl:grid-cols-4">
            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-blue-100 rounded-md dark:bg-blue-500/20">
                      <Users className="size-6 text-blue-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">{totalStudents}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Total Students</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-red-100 rounded-md dark:bg-red-500/20">
                      <Users className="size-6 text-red-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">{absentToday}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Absent Students (Today)</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-green-100 rounded-md dark:bg-green-500/20">
                      <Users className="size-6 text-green-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">{presentToday}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Present Students (Today)</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-blue-100 rounded-md dark:bg-blue-500/20">
                      <Calendar className="size-6 text-blue-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">{workingDaysInMonth}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Working Days (Current Month)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Card */}
          <div className="card">
            <div className="card-body">
              
              {/* Filters Section */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 xl:grid-cols-12 mb-5">
                
                {/* Search */}
                <div className="lg:col-span-3">
                  <div className="relative">
                    <input
                      type="text"
                      className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Search for ..."
                      autoComplete="off"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                    <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                </div>

                {/* Program Filter */}
                <div className="lg:col-span-3">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Program"
                    name="program"
                    options={programOptions}
                    value={selectedProgramOption}
                    onChange={handleProgramChange}
                    isLoading={programs.length === 0}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div>

                {/* Class Filter */}
                <div className="lg:col-span-3">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Class"
                    name="class"
                    options={classOptions}
                    value={selectedClassOption}
                    onChange={handleClassChange}
                    isLoading={classes.length === 0}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div>

                {/* Month-Year Filter */}
                <div className="lg:col-span-3">
                  <div className="relative">
                    <input
                      type="month"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      value={selectedDate}
                      onChange={handleDateChange}
                    />
                    <Calendar className="inline-block size-4 absolute ltr:right-2.5 rtl:left-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Attendance Table */}
              {isLoading || isLoadingAttendance ? (
                <Loader />
              ) : !selectedProgram?.programId || !selectedClass?.classId ? (
                <div className="text-center py-8">
                  <div className="text-slate-500 dark:text-zink-400">
                    <Calendar className="size-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">Select Program and Class</p>
                    <p>Please select both a program and class to view attendance data.</p>
                  </div>
                </div>
              ) : filteredAttendance.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-slate-500 dark:text-zink-400">
                    <Users className="size-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">No Students Found</p>
                    <p>No students found for the selected program, class, and month.</p>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <TableContainer
                    columns={columns}
                    data={filteredAttendance}
                    isPagination={false}
                    divclassName="overflow-x-auto"
                    tableclassName="w-full whitespace-nowrap"
                    theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                    thclassName="px-3 py-2 font-semibold border-b border-slate-200 dark:border-zink-500 text-center"
                    tdclassName="px-3 py-2 border-y border-slate-200 dark:border-zink-500"
                    trclassName="even:bg-slate-50 dark:odd:bg-zink-700 dark:even:bg-zink-600"
                  />
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default AttendanceList;
